import {type ClassValue, clsx} from "clsx"
import {twMerge} from "tailwind-merge"
import {
    type FeedStateEnum,
    FeedStateEnum as FeedStateEnumValue,
    LanguageLocale as LanguageLocaleConst,
    type LanguageLocale,
    OperatingModeEnum as OperatingModeEnumConst,
    type OperatingModeEnum
} from "@/api/model";
import {trans, type TranslatorKeys} from "@/lib/translator.ts";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function getRunningModeTranslatorKey(mode: OperatingModeEnum): TranslatorKeys {
    switch (mode) {
        case OperatingModeEnumConst.off:
            return 'operationModeOff'
        case OperatingModeEnumConst.priority_switch:
            return 'operationModePrioritySwitch'
        case OperatingModeEnumConst.manual:
            return 'operationModeManual'
        case OperatingModeEnumConst.timer:
            return 'operationModeTimer'
        case OperatingModeEnumConst.automatic:
            return "operationModeAutomatic";
        default:
            return 'emptyString'
    }
}

export function getFeedStateTranslatorKey(state: FeedStateEnum): TranslatorKeys {
    switch (state) {
        case FeedStateEnumValue.dry:
            return 'feedStateDry'
        case FeedStateEnumValue.post_drying:
            return 'feedStatePostDrying'
        case FeedStateEnumValue.nearly_dry:
            return 'feedStateNearlyDry'
        case FeedStateEnumValue.medium_moist:
            return 'feedStateMediumMoist'
        case FeedStateEnumValue.moist:
            return 'feedStateMoist'
        case FeedStateEnumValue.very_moist:
        default:
            return 'feedStateVeryMoist'
    }
}

export function getSwitchoverHumidity(state: FeedStateEnum): number {
    switch (state) {
        case FeedStateEnumValue.dry:
            return 40
        case FeedStateEnumValue.post_drying:
            return 30
        case FeedStateEnumValue.nearly_dry:
            return 50
        case FeedStateEnumValue.medium_moist:
            return 60
        case FeedStateEnumValue.moist:
            return 70
        case FeedStateEnumValue.very_moist:
        default:
            return 85
    }
}

export function getPauseDuration(state: FeedStateEnum): number {
    switch (state) {
        case FeedStateEnumValue.dry:
            return 10
        case FeedStateEnumValue.post_drying:
            return 8
        case FeedStateEnumValue.nearly_dry:
            return 5
        case FeedStateEnumValue.medium_moist:
            return 4
        case FeedStateEnumValue.moist:
            return 3
        case FeedStateEnumValue.very_moist:
        default:
            return 2
    }
}

export function onNavImp() {
    window.open("https://firma-stockner.com/impressum/", "_blank");
}

export function onNavPrivacy() {
    window.open("https://firma-stockner.com/datenschutz/", "_blank");
}

export function getLanguageSelectItems(l: LanguageLocale) {
    return [
        {
            value: LanguageLocaleConst.de,
            label: trans('german', l)
        },
        {
            value: LanguageLocaleConst.it,
            label: trans('italian', l)
        }
    ]
}

export function formatNumber(num: number, precision = 0): string {
    return Intl.NumberFormat('de', {
        maximumFractionDigits: precision ?? 0,
        minimumFractionDigits: precision ?? 0
    }).format(num);
}

export const WEEKDAYS: Record<LanguageLocale, Record<number, string>> = {
    de: {
        0: 'So.',
        1: 'Mo.',
        2: 'Di.',
        3: 'Mi.',
        4: 'Do.',
        5: 'Fr.',
        6: 'Sa.',
    },
    it: {
        0: 'Do.',
        1: 'Lu.',
        2: 'Ma.',
        3: 'Me.',
        4: 'Gi.',
        5: 'Ve.',
        6: 'Sa.',
    },
};
export const MONTHS: Record<LanguageLocale, Record<number, string>> = {
    de: {
        0: 'Januar',
        1: 'Februar',
        2: 'März',
        3: 'April',
        4: 'Mai',
        5: 'Juni',
        6: 'Juli',
        7: 'August',
        8: 'September',
        9: 'Oktober',
        10: 'November',
        11: 'Dezember',
    },
    it: {
        0: 'Gennaio',
        1: 'Febbraio',
        2: 'Marzo',
        3: 'Aprile',
        4: 'Maggio',
        5: 'Giugno',
        6: 'Luglio',
        7: 'Agosto',
        8: 'Settembre',
        9: 'Ottobre',
        10: 'Novembre',
        11: 'Dicembre',
    },
};

export function formatDate(
    date: Date | string | number,
    l: LanguageLocale,
    forInput = false,
    onlyTime = false,
    onlyDate = false,
    long = false,
    shortYear = false,
    noYear = false,
    onlyMonth = false,
    onlyYear = false,
    onlyWeekday = false,
    noMinutes = false,
): string {
    const d = new Date(date);
    if (typeof d === 'undefined') {
        return '';
    }
    if (forInput) {
        const iso = d.toISOString();
        if (onlyDate) {
            return iso.split('T')[0];
        }
        if (onlyTime) {
            return iso.split('T')[1];
        }
        return iso;
    }
    if (onlyTime) {
        if (long) {
            return (
                d.getHours().toString(10) +
                ':' +
                d.getMinutes().toString(10).padStart(2, '0') +
                ':' +
                d.getSeconds().toString(10).padStart(2, '0')
            );
        }
        if (noMinutes) {
            return d.getHours().toString(10)
        }
        return (
            d.getHours().toString(10) +
            ':' +
            d.getMinutes().toString(10).padStart(2, '0')
        );
    }
    let year = d.getFullYear();
    if (year >= 2000 && shortYear) {
        year -= 2000;
    }
    if (onlyYear) {
        return year.toString(10);
    }
    if (onlyDate) {
        if (onlyWeekday) {
            return WEEKDAYS[l][d.getDay()];
        }
        if (long) {
            return (
                WEEKDAYS[l][d.getDay()] +
                ' ' +
                d.getDate().toString(10).padStart(2, '0') +
                '.' +
                (d.getMonth() + 1).toString(10).padStart(2, '0') +
                (noYear ? '' : '.' + year)
            );
        }
        return (
            d.getDate().toString(10).padStart(2, '0') +
            '.' +
            (d.getMonth() + 1).toString(10).padStart(2, '0') +
            (noYear ? '' : '.' + year)
        );
    }
    if (onlyMonth) {
        return MONTHS[l][d.getMonth()].substring(0, 3) + '.'
    }
    if (onlyWeekday) {
        return WEEKDAYS[l][d.getDay()] + " " +
            d.getHours().toString(10) +
            (noMinutes ? '' : ':' +
                d.getMinutes().toString(10).padStart(2, '0'))

    }
    return (
        d.getDate().toString(10).padStart(2, '0') +
        '.' +
        (d.getMonth() + 1).toString(10).padStart(2, '0') +
        (noYear ? '' : '.' + year) +
        ', ' +
        d.getHours().toString(10) +
        (noMinutes ? '' : ':' +
            d.getMinutes().toString(10).padStart(2, '0'))
    );
}


export function convertTimeToString(value: number, isHours: boolean, long: boolean, l: LanguageLocale, isSeconds?: boolean): string {
    if (isHours) {
        if (value === Math.floor(value)) {
            return `${value} ${trans(long ? 'hours' : 'hoursShort', l)}`
        } else {
            const minutes = Math.floor((value - Math.floor(value)) * 60)
            return `${Math.floor(value)} ${trans(long ? 'hours' : 'hoursShort', l)} ${minutes} ${trans(long ? 'minutes' : 'minutesShort', l)}`
        }
    } else if (isSeconds) {
        const day = 3600 * 24;
        if (value > day) {
            const hours = Math.floor(value / 3600) % 24;
            const days = Math.floor(value / day);
            return `${days} ${trans(long ? 'days' : 'daysShort', l)} ${hours} ${trans(long ? 'hours' : 'hoursShort', l)}`;
        } else if (value > 3600) {
            const minutes = Math.floor(value / 60) % 60;
            const hours = Math.floor(value / 3600);
            return `${hours} ${trans(long ? 'hours' : 'hoursShort', l)} ${minutes} ${trans(long ? 'minutes' : 'minutesShort', l)}`;
        } else if (value > 60) {
            const seconds = value % 60;
            const minutes = Math.floor(value / 60);
            return `${minutes} ${trans(long ? 'minutes' : 'minutesShort', l)} ${seconds} ${trans(long ? 'seconds' : 'secondsShort', l)}`
        } else {
            return `${value} ${trans(long ? 'seconds' : 'secondsShort', l)}`
        }
    } else {
        if (value > 60) {
            const minutes = value % 60;
            const hours = Math.floor(value / 60);
            return `${hours} ${trans(long ? 'hours' : 'hoursShort', l)} ${minutes} ${trans(long ? 'minutes' : 'minutesShort', l)}`
        } else {
            return `${value} ${trans(long ? 'minutes' : 'minutesShort', l)}`
        }
    }
}

export function convertTimeToShortString(time: string): string {
    const parts = time.split(':');
    if (parts.length === 3) {
        return time.replace(/:\d{2}$/, "");
    }
    return time;
}
